import { rawOAuth } from "../../utils";
import {
  GenerateMockupPayload,
  MockupGenerationCompletedResponse,
  MockupGenerationFailedResponse,
  MockupGenerationResponse,
  ProductDecorationRequest,
} from "./types";
import { makeJobStorage, pollUntilTerminal } from "./utils";


const singleMockupJobStorage = makeJobStorage("mockup_generation_job_id:");
const pendingFeedbackStorage = makeJobStorage("mockup_pending_feedback:");

export function hasInFlightMockupJob(itemId: string): boolean {
  return !!singleMockupJobStorage.read(itemId);
}

export function persistPendingFeedback(itemId: string, jobId: string): void {
  pendingFeedbackStorage.persist(itemId, jobId);
}

export function readPendingFeedback(itemId: string): string | null {
  return pendingFeedbackStorage.read(itemId);
}

export function clearPendingFeedback(itemId: string): void {
  pendingFeedbackStorage.clear(itemId);
}

const keysToIgnore = Object.freeze([
  "item_location_id",
  "ps_decoration_id",
  "item_decoration_id",
  "imprint_id",
  "imprint_name",
  "ext_decoration_id",
  "ext_location_id",
]);

function throwForFailedMockupResponse(resp: MockupGenerationFailedResponse): never {
  throw new Error(resp.error || "Unable to generate image. Please try again.");
}

async function getSingleJobStatus(jobId: string): Promise<MockupGenerationResponse> {
  const res = await rawOAuth(
    "GET",
    "mockup-generation",
    { job_id: jobId },
    null,
    { Accept: "application/json" },
  );
  const json = (await res.json()) as MockupGenerationResponse;
  if (!res.ok) {
    return {
      status: "failed",
      job_id: jobId,
      error: (json as MockupGenerationFailedResponse)?.error || "Unable to fetch mockup status",
    };
  }
  return json;
}

const inFlightByItemId = new Map<string, Promise<MockupGenerationCompletedResponse | null>>();

/**
 * Resume polling for a single-item mockup job that was persisted in localStorage
 * (e.g. the user navigated away mid-generation and came back).
 * Returns null if no persisted job exists for this item.
 */
export async function resumeMockupGenerationForItem(args: {
  itemId: string;
  timeoutMs?: number;
}): Promise<MockupGenerationCompletedResponse | null> {
  const { itemId, timeoutMs = 120000 } = args;
  const existing = inFlightByItemId.get(itemId);
  if (existing) return existing;

  const jobId = singleMockupJobStorage.read(itemId);
  if (!jobId) return null;

  const promise = (async () => {
    const current = await getSingleJobStatus(jobId);
    if (current.status === "completed") {
      singleMockupJobStorage.clear(itemId);
      return current;
    }
    if (current.status === "failed") {
      singleMockupJobStorage.clear(itemId);
      throwForFailedMockupResponse(current);
    }

    const polled = await pollUntilTerminal(
      () => getSingleJobStatus(jobId),
      () => ({ status: "failed" as const, job_id: jobId, error: "Mockup generation timed out" }),
      { timeoutMs, initialPollMs: current.poll_after_ms },
    );
    singleMockupJobStorage.clear(itemId);
    if (polled.status === "completed") return polled;
    if (polled.status === "failed") throwForFailedMockupResponse(polled);
    return null;
  })().finally(() => {
    inFlightByItemId.delete(itemId);
  });

  inFlightByItemId.set(itemId, promise);
  return promise;
}

export async function generateMockupForItem(args: {
  itemId: string;
  productImageUrl: string;
  logoImageUrl: string | undefined;
  instructions: string;
  productDecorations: ProductDecorationRequest;
  timeoutMs?: number;
}): Promise<MockupGenerationCompletedResponse> {
  const { itemId, productImageUrl, logoImageUrl, instructions, productDecorations, timeoutMs = 120000 } = args;

  // If there's already an in-flight job for this item (e.g. modal reopened), reuse it.
  const existing = await resumeMockupGenerationForItem({ itemId, timeoutMs });
  if (existing?.file) return existing;

  if (!productDecorations.decoration_name || !productDecorations.location_name) {
    throw new Error("Please write a product decoration and location before generating an image.");
  }

  const structured_product_data = Object.entries(productDecorations)
    .filter(([key, value]) => value !== undefined && !keysToIgnore.includes(key))
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");

  const payload: GenerateMockupPayload = {
    action: "generate_mockup",
    item_id: itemId,
    product_image_url: productImageUrl,
    logo_image_url: logoImageUrl,
    structured_product_data,
    additional_instructions: instructions || "",
  };

  const res = await rawOAuth("POST", "mockup-generation", null, payload, { Accept: "application/json" });
  const json = (await res.json()) as MockupGenerationResponse;
  if (!res.ok) {
    throw new Error((json as MockupGenerationFailedResponse)?.error || "Unable to generate image. Please try again.");
  }

  if (json.status === "completed") return json;
  if (json.status === "failed") throwForFailedMockupResponse(json);

  singleMockupJobStorage.persist(itemId, json.job_id);
  const polled = await pollUntilTerminal(
    () => getSingleJobStatus(json.job_id),
    () => ({ status: "failed" as const, job_id: json.job_id, error: "Mockup generation timed out" }),
    { timeoutMs, initialPollMs: json.poll_after_ms },
  );
  singleMockupJobStorage.clear(itemId);
  if (polled.status === "completed") return polled;
  throwForFailedMockupResponse(polled as MockupGenerationFailedResponse);
}
